import { Button } from '@vrplatform/components';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Modal } from '@vrplatform/components-legacy';
import { showErrorNotification } from '@vrplatform/theme';
import { waitFor } from '@vrplatform/utils';
import { Box, Center, Stack, Text } from '@mantine/core';
import type { InputFormSchema, SchemaOutputType } from '@vrplatform/ui-common';
import { type ElementType, type ReactNode, useEffect, useState } from 'react';
import {
  type SubmitErrorHandler,
  type UseFormReturn,
  useForm,
} from 'react-hook-form';
import { LegacyInputsBySchema } from '../legacy-inputs-by-schema';
import { ErrorScreen, SuccessScreen } from './_screens';

export type LegacyConfirmModalProps<T extends InputFormSchema> = {
  autoCloseOnSuccess?: boolean | number | string;
  autoSubmit?: boolean;
  opened: boolean;
  onSuccess?: (result: SchemaOutputType<T>) => void;
  onClose?: (success: boolean) => void;
  title: string;
  subtitle?: ReactNode;
  onSubmit: LegacySubmitConfirmModalType<SchemaOutputType<T>>;
  icon?: ReactNode;
  schema?: T | null;
  size?: string | number;
  zIndex?: number;
  submitButtonTitle?: string;
  withCloseButton?: boolean;
  value?: LegacyConfirmResultType;
  customSubmitBody?: ElementType<{
    methods: UseFormReturn<SchemaOutputType<T>, any>;
    submit: (onInvalid?: SubmitErrorHandler<SchemaOutputType<T>>) => void;
  }>;
};

export type LegacyConfirmResultType = {
  message?: string;
  isSuccess: boolean;
  autoCloseOnSuccess?: LegacyConfirmModalProps<any>['autoCloseOnSuccess'];
};
export type LegacySubmitConfirmModalType<T = any> = (
  value?: T
) => Promise<LegacyConfirmResultType>;

export const LegacyConfirmModal = <T extends InputFormSchema>({
  opened,
  onClose: $onClose,
  onSuccess,
  title,
  subtitle,
  onSubmit,
  autoSubmit,
  icon,
  schema,
  autoCloseOnSuccess,
  size = 'sm',
  submitButtonTitle = 'Submit',
  value,
  customSubmitBody: CustomSubmitBody,
  withCloseButton = false,
  zIndex,
}: LegacyConfirmModalProps<T>) => {
  const [r, setResult] = useState<LegacyConfirmResultType>({
    isSuccess: false,
    message: undefined,
    autoCloseOnSuccess: undefined,
  });
  const result = value || r;

  const [loading, setLoading] = useState(false);

  const methods = useForm<SchemaOutputType<T>>({ mode: 'onChange' });

  const submit = async (data: SchemaOutputType<T>) => {
    setLoading(true);
    try {
      const result = await onSubmit(data);
      setResult(result);
      if (result.isSuccess) {
        if (onSuccess) onSuccess(data);
        const $autoCloseOnSuccess =
          result.autoCloseOnSuccess || autoCloseOnSuccess;
        if ($autoCloseOnSuccess) {
          await waitFor(
            $autoCloseOnSuccess === true ? '1s' : $autoCloseOnSuccess
          );
          onClose();
        }
      }
      /*showSuccessNotification({
        message:
          'Your action was successfull! This notification will close in 2 seconds.',
        title: 'Success!',
      });*/
    } catch (error: any) {
      showErrorNotification({
        title: 'Please try again.',
        message: error?.message || error || 'Something went wrong...',
      });
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    setTimeout(() => {
      setLoading(false);
      setResult({ isSuccess: false, message: undefined });
      methods.reset();
    }, 300);
    if ($onClose) $onClose(result.isSuccess);
  };

  const reset = () => {
    setLoading(false);
    setResult({ isSuccess: false, message: undefined });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (autoSubmit && opened) {
      submit({} as any);
    }
  }, [autoSubmit, opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={size}
      withCloseButton={withCloseButton}
      title={withCloseButton && title}
      zIndex={zIndex}
    >
      <Box
        mb="sm"
        sx={{
          minHeight: icon ? 250 : undefined,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: !CustomSubmitBody ? 'flex-end' : undefined,
        }}
      >
        {!result?.message ? (
          loading ? (
            // LOADING SCREEN
            <Center sx={{ flex: 1, minHeight: 250 }}>
              <LoadingIndicator
                isFullPageLoading={false}
                message="Loading..."
              />
            </Center>
          ) : CustomSubmitBody ? (
            <CustomSubmitBody
              methods={methods}
              submit={(onInvalid) => methods.handleSubmit(submit, onInvalid)()}
            />
          ) : (
            // SUBMIT SCREEN
            <>
              <Stack align="center" mb={20}>
                {icon && <Center mt={10}>{icon}</Center>}
                <Box
                  mb={10}
                  mt={15}
                  sx={{ textAlign: 'center', width: '100%' }}
                >
                  {!withCloseButton && title && schema && (
                    <Text fw={500} mb={10}>
                      {title}
                    </Text>
                  )}
                  {schema && (
                    <LegacyInputsBySchema
                      schema={schema}
                      methods={methods as any}
                    />
                  )}
                  {subtitle && <Text size="sm">{subtitle}</Text>}
                </Box>
              </Stack>

              <Button
                variant="primary"
                onClick={methods.handleSubmit(submit)}
                type="submit"
                test-id="confirm-modal-submit-button"
                disabled={!!schema && !methods.formState.isValid}
                sx={{ height: 40, width: '100%', marginBottom: 10 }}
              >
                {submitButtonTitle}
              </Button>
              <Button
                variant="light"
                color="neutral"
                onClick={onClose}
                test-id="confirm-modal-close-button"
                sx={{
                  width: '100%',
                }}
              >
                Cancel
              </Button>
            </>
          )
        ) : result.isSuccess ? (
          // SUCCESS SCREEN
          <SuccessScreen onClose={onClose} resultMessage={result.message} />
        ) : (
          // ERROR SCREEN
          <ErrorScreen
            onClose={onClose}
            resultMessage={result.message}
            onSubmit={reset}
          />
        )}
      </Box>
    </Modal>
  );
};
